import { useAtomValue } from 'jotai';
import { lazy, Suspense } from 'react';
import { Outlet } from '@tanstack/react-router';

import { Loading } from '@crossnokaye/ux-common';

import { AgentStatusBanner } from '@/components/agent-status-banner/agent-status-banner';
import { AgentStatusContentGate } from '@/components/agent-status-splash/agent-status-content-gate';
import { Banner } from '@/components/banner';
import { BetaCompareButton } from '@/components/beta-compare-button';
import { BranchEnvIndicator } from '@/components/branch-env-indicator';
import { LeftNavigation, TopNavigation } from '@/components/navigation';
import { RightPane } from '@/components/right-pane';
import { StreamStatusBanner } from '@/components/stream-status-banner/stream-status-banner';
import { FACILITY_FRAME_ID } from '@/constants';
import { DeveloperTools } from '@/features/developer-tools/developer-tools';
import {
  queryDevToolsVisibleAtom,
  routerDevToolsVisibleAtom,
} from '@/features/developer-tools/third-party-tools/third-party-devtools.state';
import { EquipmentDrawerLoader } from '@/features/equipment-browser/equipment-drawer-loader/equipment-drawer-loader';
import { useAlarmsStream } from '@/hooks/query-hooks/streams/gaia-api/use-alarms-stream';
import { useReadingsStream } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { useSettingsStream } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';
import { useShutdownBannerStream } from '@/hooks/query-hooks/streams/gaia-api/use-shutdown-banner-stream';
import { useUserActivityStream } from '@/hooks/query-hooks/streams/gaia-api/use-user-activity-stream';
import { useAnalyticsIdentify } from '@/hooks/use-analytics-identify';
import { useAuth } from '@/hooks/use-auth';
import { useExternalScripts } from '@/hooks/use-external-scripts';
import { useLaunchDarklyContext } from '@/hooks/use-launch-darkly-context';
import { Stack } from '@/ux-common/components/stack/stack';

import {
  APP_CONTAINER_ID,
  BOTTOM_STICKY_PORTAL_ID,
  PAGE_CONTENT_CONTAINER_ID,
  SCROLL_CONTAINER_ID,
  TOP_NAVIGATION_CONTAINER_ID,
} from './app-layout.constants';
import { AppContainer } from './components/app-container';
import { FacilityFrame } from './components/facility-frame';
import { PageContentContainer } from './components/page-content-container';
import { ScrollContainer } from './components/scroll-container';
import { TopNavigationContainer } from './components/topnav-container';
import { useInitLayout } from './hooks/use-init-layout';
import { useScrollPositionTracking } from './hooks/use-scroll-position-tracking';

const LazyReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((m) => ({
    default: m.ReactQueryDevtools,
  }))
);

const LazyRouterDevtools = lazy(() =>
  import('@tanstack/react-router-devtools').then((m) => ({
    default: m.TanStackRouterDevtools,
  }))
);

const QueryDevToolsGated = () => {
  const visible = useAtomValue(queryDevToolsVisibleAtom);
  if (!visible) return null;
  return (
    <Suspense fallback={null}>
      <LazyReactQueryDevtools initialIsOpen={false} />
    </Suspense>
  );
};

const RouterDevToolsGated = () => {
  const visible = useAtomValue(routerDevToolsVisibleAtom);
  if (!visible) return null;
  return (
    <Suspense fallback={null}>
      <LazyRouterDevtools position="bottom-right" />
    </Suspense>
  );
};

/**
 * AppLayout - Main application layout component.
 * Responsible only for layout structure, navigation, and UI components.
 * Its only business logic
 */
export const AppLayout = () => {
  // Determine authentication state
  const { isAuthenticated } = useAuth();

  // Initialize layout
  useInitLayout();
  // Track scroll position
  useScrollPositionTracking();
  // Global unidirectional websockets
  useUserActivityStream();
  useAlarmsStream();
  useShutdownBannerStream();

  // Global bi-directional websockets
  useReadingsStream();
  useSettingsStream();
  // Set LaunchDarkly context
  useLaunchDarklyContext();
  // Set Analytics User Properties
  useAnalyticsIdentify();
  // Initialize external scripts (Pendo and Zendesk)
  useExternalScripts();

  // Display full-page loading state unless we are positively authenticated.
  if (!isAuthenticated) {
    return (
      <div className={'flex flex-col h-screen w-screen justify-center items-center'}>
        <Loading size={'xl'} />
      </div>
    );
  }

  // Display actual application layout once authenticated.
  return (
    <BranchEnvIndicator>
      {/* the main container for the facility.  Wraps entire screen. */}
      <FacilityFrame id={FACILITY_FRAME_ID}>
        <Banner />
        <StreamStatusBanner />
        <AgentStatusBanner />

        {/* the app container horizontally contains the left navigation, scroll container, and right pane */}
        <AppContainer id={APP_CONTAINER_ID}>
          <LeftNavigation />

          {/* the scroll container contains the vertically scrollable page content. */}
          <Stack className="stack-none flex-1">
            <TopNavigationContainer id={TOP_NAVIGATION_CONTAINER_ID}>
              <TopNavigation />
            </TopNavigationContainer>
            <ScrollContainer id={SCROLL_CONTAINER_ID}>
              {/* the page content container contains the page content. */}
              <PageContentContainer id={PAGE_CONTENT_CONTAINER_ID}>
                <AgentStatusContentGate>
                  <EquipmentDrawerLoader>
                    <Outlet />
                  </EquipmentDrawerLoader>
                </AgentStatusContentGate>
              </PageContentContainer>
            </ScrollContainer>
            <div id={BOTTOM_STICKY_PORTAL_ID}></div>
          </Stack>

          <RightPane />
        </AppContainer>
        {/* optional dev tools */}
        <BetaCompareButton />
        <QueryDevToolsGated />
        <RouterDevToolsGated />
        <DeveloperTools />
      </FacilityFrame>
    </BranchEnvIndicator>
  );
};
